import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { connect } from 'react-redux';
import { iRootDispatch, iRootState } from '../../../../store';
import { AvailableBrokersAndEstatesState } from '../../../../store/types/temp-types';
import { unmaskCpfOrCnpj } from '../../../../services/masks';
import FranchiseListPageTable from '../Table';
import { useQuery } from '@tanstack/react-query';
import { getMasterFranchisesList } from '../../../../services/franchises';
import { MasterFranchiseState } from '../../../../types/FranchiseState';
import { statusOptions, typeOptions } from '../../types';
import ViewModal from '../ViewModal';
import { dangerNotification } from '../../../../services/notification';
import { MasterFranchiseListPageProps } from '../..';
import FilterBar, { FilterField, FilterValues } from '../../../../components/Filter';

type SearchField = 'document' | 'name' | 'address';

type ApiFilters = {
  type?: string;
  argument?: string;
  status?: string;
};

const SEARCH_FIELDS: SearchField[] = ['document', 'name', 'address'];

const initialFilterValues: FilterValues = {
  type: 'name',
  argument: '',
  status: ''
};

const toSelectOptions = (
  items: Array<{ value: any; text: string }>
): Array<{ value: string; label: string }> =>
  items.map(item => ({
    value: String(item.value),
    label: String(item.text)
  }));

const normalizeToApiFilter = (values: FilterValues): ApiFilters => {
  const searchType = (values.type as SearchField) || 'name';
  let argument = String(values.argument || '').trim();

  if (searchType === 'document' && argument) {
    argument = unmaskCpfOrCnpj(argument);
  }

  const filter: ApiFilters = {};

  if (argument && SEARCH_FIELDS.includes(searchType)) {
    filter.type = searchType;
    filter.argument = argument;
  }

  if (values.status) filter.status = String(values.status);

  return filter;
};

const mapState = (state: iRootState) => ({
  availableBrokersAndEstates: state.availableBrokersAndEstates,
  user: state.user
});

const mapDispatch = (dispatch: iRootDispatch) => ({
  updateAvailableBrokersAndEstates: (availableBrokers: AvailableBrokersAndEstatesState) =>
    dispatch.availableBrokersAndEstates.updateAvailableBrokersAndEstates(availableBrokers)
});

const MasterFranchiseListPage = (props: MasterFranchiseListPageProps) => {
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [itemSelected, setItemSelected] = useState<MasterFranchiseState | null>(null);
  const [isViewModalOpen, setIsViewModalOpen] = useState<boolean>(false);
  const [total, setTotal] = useState(0);

  const [appliedFilters, setAppliedFilters] = useState<ApiFilters>(() =>
    normalizeToApiFilter(initialFilterValues)
  );
  const appliedFiltersRef = useRef(appliedFilters);

  useEffect(() => {
    appliedFiltersRef.current = appliedFilters;
  }, [appliedFilters]);

  const filterFields: FilterField[] = useMemo(
    () => [
      {
        kind: 'search',
        name: 'search',
        label: 'Pesquisar',
        placeholder: 'Digite...',
        defaultType: 'name',
        types: typeOptions.map(option => ({
          value: String(option.value),
          label: String(option.text)
        })),
        width: 1.6
      },
      {
        kind: 'select',
        name: 'status',
        label: 'Status',
        placeholder: 'Selecionar...',
        options: toSelectOptions(statusOptions),
        getDisplayValue: values => {
          const option = statusOptions.find(
            item => String(item.value) === String(values.status)
          );
          return option ? option.text : undefined;
        }
      }
    ],
    []
  );

  const getData = useCallback(async () => {
    try {
      const filters = {
        page: `${currentPage}`,
        ...appliedFiltersRef.current
      };
      const franchises = await getMasterFranchisesList(filters);
      if (currentPage === 1) setTotal(franchises.count);
      return franchises;
    } catch (e) {
      dangerNotification('Oops...', 'Não foi possível buscar os parceiros!');
      throw e;
    }
  }, [currentPage]);

  const { data, isFetching: isLoading } = useQuery(
    ['getMasterFranchisesList', currentPage, appliedFilters],
    getData,
    {
      enabled: true,
      keepPreviousData: true,
      refetchOnMount: 'always'
    }
  );

  const handleFilterSearch = (values: FilterValues) => {
    setAppliedFilters(normalizeToApiFilter(values));
    setCurrentPage(1);
  };

  const handleFilterClear = () => {
    setCurrentPage(1);
  };

  return (
    <>
      <div style={{ marginBottom: 16 }}>
        <FilterBar
          fields={filterFields}
          initialValues={initialFilterValues}
          onSearch={handleFilterSearch}
          onClear={handleFilterClear}
          loading={isLoading}
          syncToUrl
          urlNamespace="master-franchises"
        />
      </div>

      <FranchiseListPageTable
        multiFranchises={data?.data || []}
        isLoading={isLoading}
        currentPage={currentPage}
        setCurrentPage={setCurrentPage}
        dataCount={total}
        handleSelectItem={(item: MasterFranchiseState) => {
          setItemSelected(item);
          setIsViewModalOpen(true);
        }}
      />
      {itemSelected && (
        <ViewModal
          isOpen={isViewModalOpen}
          onClose={() => setIsViewModalOpen(false)}
          multiFranchise={itemSelected}
        />
      )}
    </>
  );
};

export default connect(mapState, mapDispatch)(MasterFranchiseListPage);
